import { useCallback, useEffect, useRef, useState } from 'react'
import { formatPrice } from '../../base/utils/dataHelper'
import {
  GetSerialNumberCheckResponse,
  IGetOrderDetailsResponse,
} from '../../pages/MySpecial/MyOrders/models/response/getOrderListResponseModel'
import { GetOrderDetailsApi } from '../../pages/MySpecial/MyOrders/services/getOrderDetails'
import { useAppSelector } from '../../redux/app/hooks'
import { currentUser } from '../../redux/slices/user/userSlice'
import classes from './ChatWidget.module.scss'
import { displayValue, getChatErrorMessage } from './chatFormatters'
import { IconBarcode } from './icons'
import OrderSerialNumberModal from './OrderSerialNumberModal'
import { OrderSerialNumberApi, resolveOrderSerialNumberRequest } from './orderSerialNumberService'
import { ChatOrder } from './types'

type LoadStatus = 'idle' | 'loading' | 'success' | 'error'

interface LoadState<T> {
  status: LoadStatus
  data: T[]
  error: string | null
}

const createInitialLoadState = <T,>(): LoadState<T> => ({
  status: 'idle',
  data: [],
  error: null,
})

/** Sipariş detayındaki URUN_KODU ile seri no servisinin ProductCode alanını toleranslı eşleştirir. */
const normalizeProductCode = (value?: string | null): string => (value || '').trim().toUpperCase()

interface Props {
  order: ChatOrder
  open: boolean
  disabled?: boolean
}

/** Sipariş ürünleri ile bunlara ait seri numaralarını iki sipariş akışında ortak gösterir. */
export default function OrderDetailsPanel({ order, open, disabled }: Props) {
  const user = useAppSelector(currentUser)
  const mountedRef = useRef(true)
  const [details, setDetails] = useState<LoadState<IGetOrderDetailsResponse>>(() => createInitialLoadState())
  const [serialNumbers, setSerialNumbers] = useState<LoadState<GetSerialNumberCheckResponse>>(() =>
    createInitialLoadState()
  )
  /** Seri no modalı açık olan ürün; null ise modal kapalı. */
  const [activeSerialProduct, setActiveSerialProduct] = useState<{ code: string; name: string } | null>(null)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const loadDetails = useCallback(async () => {
    setDetails({ status: 'loading', data: [], error: null })

    try {
      const response = await new GetOrderDetailsApi({} as any).getOrderDetail({
        OrderDocNo: order.orderNumber,
      })

      if (!mountedRef.current) return
      setDetails({ status: 'success', data: Array.isArray(response) ? response : [], error: null })
    } catch (requestError: unknown) {
      if (!mountedRef.current) return
      setDetails({
        status: 'error',
        data: [],
        error: getChatErrorMessage(requestError, 'Sipariş detayı şu anda alınamıyor.'),
      })
    }
  }, [order.orderNumber])

  useEffect(() => {
    if (open && details.status === 'idle') void loadDetails()
  }, [details.status, loadDetails, open])

  const loadSerialNumbers = async () => {
    if (disabled || serialNumbers.status === 'loading') return

    setSerialNumbers({ status: 'loading', data: [], error: null })
    try {
      const request = await resolveOrderSerialNumberRequest(order, user.AccountCode)
      if (!request) {
        if (!mountedRef.current) return
        setSerialNumbers({
          status: 'error',
          data: [],
          error: 'Siparişin seri no sorgusu için şirket veya fatura bilgisi bulunamadı.',
        })
        return
      }

      const response = await new OrderSerialNumberApi().list(request)
      if (!mountedRef.current) return
      setSerialNumbers({ status: 'success', data: Array.isArray(response) ? response : [], error: null })
    } catch (requestError: unknown) {
      if (!mountedRef.current) return
      setSerialNumbers({
        status: 'error',
        data: [],
        error: getChatErrorMessage(requestError, 'Seri numaraları şu anda alınamıyor.'),
      })
    }
  }

  /** Satırdaki barkod butonu: modalı açar, seri no'lar henüz yüklenmediyse tek seferlik sorguyu tetikler. */
  const openSerialModal = (line: IGetOrderDetailsResponse) => {
    if (disabled) return
    setActiveSerialProduct({ code: (line.URUN_KODU || '').trim(), name: line.URUN_ACIKLAMA || '' })
    if (serialNumbers.status === 'idle' || serialNumbers.status === 'error') void loadSerialNumbers()
  }

  if (!open) return null

  const currency = order.currency
  // Servis siparişin tamamını fatura bazında döndürür; modal yalnızca seçilen ürünün kayıtlarını gösterir.
  const activeSerials = activeSerialProduct
    ? serialNumbers.data.filter(
        item => normalizeProductCode(item.ProductCode) === normalizeProductCode(activeSerialProduct.code)
      )
    : []

  return (
    <section className={classes.orderDetailPanel} aria-label={`${order.orderNumber} sipariş içeriği`}>
      <div className={classes.orderDetailHeading}>
        <strong>Sipariş içeriği</strong>
        {details.status === 'success' && details.data.length > 0 && (
          <span className={classes.orderDetailHint}>Seri/IMEI için ürün satırındaki butona tıklayın</span>
        )}
      </div>

      {details.status === 'loading' && (
        <div className={classes.orderDetailStatus} role='status' aria-live='polite'>
          Sipariş içeriği yükleniyor…
        </div>
      )}

      {details.status === 'error' && (
        <div className={classes.orderDetailError} role='alert'>
          <span>{details.error}</span>
          <button type='button' onClick={loadDetails} disabled={disabled}>
            Tekrar dene
          </button>
        </div>
      )}

      {details.status === 'success' && details.data.length === 0 && (
        <div className={classes.orderDetailStatus}>Sipariş detayı bulunamadı.</div>
      )}

      {details.status === 'success' && details.data.length > 0 && (
        <div className={classes.orderDetailTableWrap} tabIndex={0} aria-label='Sipariş ürünleri tablosu'>
          <table className={classes.orderDetailTable}>
            <thead>
              <tr>
                <th scope='col'>Ürün</th>
                <th scope='col'>Adet / tutar</th>
                <th scope='col' className={classes.orderDetailSerialCol}>
                  Seri no
                </th>
              </tr>
            </thead>
            <tbody>
              {details.data.map((line, index) => (
                <tr key={`${line.URUN_KODU || 'urun'}-${index}`}>
                  <td>
                    <strong>{displayValue(line.URUN_ACIKLAMA)}</strong>
                    <span>{displayValue(line.URUN_KODU)}</span>
                  </td>
                  <td>
                    <strong>
                      {line.TOPLAM_ADET} × {formatPrice(line.BIRIM_TUTAR, currency, 'symbol')}
                    </strong>
                    <span>{formatPrice(line.TOPLAM_TUTAR, currency, 'symbol')}</span>
                  </td>
                  <td className={classes.orderDetailSerialCol}>
                    <button
                      type='button'
                      className={classes.orderDetailSerialButton}
                      onClick={() => openSerialModal(line)}
                      disabled={disabled}
                      title='Seri / IMEI numaralarını göster'
                      aria-label={`${displayValue(line.URUN_ACIKLAMA)} için seri / IMEI numaralarını göster`}
                    >
                      <IconBarcode />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeSerialProduct && (
        <OrderSerialNumberModal
          productName={activeSerialProduct.name}
          productCode={activeSerialProduct.code}
          status={serialNumbers.status}
          serials={activeSerials}
          error={serialNumbers.error}
          onRetry={loadSerialNumbers}
          onClose={() => setActiveSerialProduct(null)}
        />
      )}
    </section>
  )
}
